import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './ProductCarousel.css';
import { logCarouselInteraction, logVideoInteraction } from '../utils/tracking';
import { ChevronLeftIcon, ChevronRightIcon } from './icons';

/**
 * ProductCarousel - Single card carousel with 3D swivel animation
 * Displays one product card at a time with left/right navigation
 * If videoUrl is provided, it will be the first card in the carousel
 */
function ProductCarousel({ products, videoUrl, trendId, summarySections = [], onSummarySelect }) {
  // Create items array: video first (if exists), then products
  const items = useMemo(() => {
    const itemsList = [];
    if (summarySections && summarySections.length > 0) {
      summarySections.forEach((section, idx) => {
        itemsList.push({ 
          type: 'summary', 
          heading: section.heading, 
          body: section.body, 
          summaryIndex: idx 
        });
      });
    }
    if (videoUrl) {
      itemsList.push({ type: 'video', url: videoUrl });
    }
    itemsList.push(...products.map(p => ({ type: 'product', ...p })));
    return itemsList;
  }, [products, videoUrl]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState('next'); // 'next' or 'prev'
  const [isAnimating, setIsAnimating] = useState(false);
  const videoRef = useRef(null);
  
  // Debug: Log items array when it changes
  useEffect(() => {
    if (items.length > 0) {
      console.log('Carousel items updated:', items.map((item, idx) => ({ 
        index: idx, 
        type: item.type, 
        hasUrl: !!item.url,
        url: item.url ? item.url.substring(0, 50) + '...' : null
      })));
    }
  }, [items]);
  

  // Clean markdown formatting from text - fix malformed patterns
  const cleanText = (text) => {
    if (!text) return '';
    const digitEmojiMap = {
      '0️⃣': '0.',
      '1️⃣': '1.',
      '2️⃣': '2.',
      '3️⃣': '3.',
      '4️⃣': '4.',
      '5️⃣': '5.',
      '6️⃣': '6.',
      '7️⃣': '7.',
      '8️⃣': '8.',
      '9️⃣': '9.'
    };
    Object.entries(digitEmojiMap).forEach(([emoji, replacement]) => {
      const regex = new RegExp(emoji, 'g');
      text = text.replace(regex, `${replacement} `);
    });
    text = text.replace(/([^\s])\*\*/g, '$1 **');
    text = text.replace(/\*\*([^\s])/g, '** $1');
    text = text.replace(/([^\s])\*/g, '$1 *');
    text = text.replace(/\*([^\s])/g, '* $1');
    text = text.replace(/\*\*/g, '');
    text = text.replace(/\*/g, '');
    text = text.replace(/\s+\*\s+/g, ' ');
    return text.replace(/\s+/g, ' ').trim();
  };

  const formatSummarySnippet = useCallback((text) => {
    if (!text) return '';
    const snippet = cleanText(text);
    const sentenceMatches = snippet.match(/[^.!?]+[.!?]+/g);
    if (sentenceMatches && sentenceMatches.length > 0) {
      const selected = sentenceMatches.slice(0, 2).join(' ').trim();
      return selected.endsWith('.') || selected.endsWith('!') || selected.endsWith('?') ? selected : `${selected}.`;
    }
    if (snippet.length <= 320) {
      return snippet;
    }
    return snippet.slice(0, 320).replace(/\s+\S*$/, '') + '…';
  }, [cleanText]);

  const renderSummaryCard = (item) => (
    <div className="carousel-card-summary">
      <div className="carousel-card-summary-label">Fresh Drop</div>
      <div className="carousel-card-summary-heading">
        {item.heading && item.heading.toLowerCase() !== 'the trend' ? cleanText(item.heading) : ''}
      </div>
      <div className="carousel-card-summary-lede">{formatSummarySnippet(item.body)}</div>
      <button 
        className="carousel-card-summary-link"
        onClick={() => onSummarySelect && onSummarySelect()}
      >
        Dive Deeper
      </button>
    </div>
  );

  const goToNext = useCallback(() => {
    if (isAnimating || items.length === 0) return;
    setDirection('next');
    setIsAnimating(true);
    setTimeout(() => {
      const newIndex = (currentIndex + 1) % items.length;
      setCurrentIndex(newIndex);
      setIsAnimating(false);
      
      // Log carousel interaction - ensure we get the correct type
      if (trendId && items.length > newIndex) {
        const targetItem = items[newIndex];
        // Explicitly check: if index is 0 and videoUrl exists, it's a video
        let cardType = 'product';
        if (newIndex === 0 && videoUrl) {
          cardType = 'video';
        } else if (targetItem && targetItem.type) {
          cardType = targetItem.type;
        }
        console.log(`Carousel next: index=${newIndex}, type=${cardType}, hasVideo=${!!videoUrl}, itemType=${targetItem?.type}`);
        logCarouselInteraction(
          trendId,
          newIndex,
          cardType,
          'next'
        );
      }
    }, 400); // Half of animation duration (0.8s / 2)
  }, [isAnimating, items, currentIndex, trendId, videoUrl]);

  const goToPrev = useCallback(() => {
    if (isAnimating || items.length === 0) return;
    setDirection('prev');
    setIsAnimating(true);
    setTimeout(() => {
      const newIndex = (currentIndex - 1 + items.length) % items.length;
      setCurrentIndex(newIndex);
      setIsAnimating(false);
      
      // Log carousel interaction - ensure we get the correct type
      if (trendId && items.length > newIndex) {
        const targetItem = items[newIndex];
        // Explicitly check: if index is 0 and videoUrl exists, it's a video
        let cardType = 'product';
        if (newIndex === 0 && videoUrl) {
          cardType = 'video';
        } else if (targetItem && targetItem.type) {
          cardType = targetItem.type;
        }
        console.log(`Carousel prev: index=${newIndex}, type=${cardType}, hasVideo=${!!videoUrl}, itemType=${targetItem?.type}`);
        logCarouselInteraction(
          trendId,
          newIndex,
          cardType,
          'prev'
        );
      }
    }, 400); // Half of animation duration (0.8s / 2)
  }, [isAnimating, items, currentIndex, trendId, videoUrl]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isAnimating, items.length, goToNext, goToPrev]);

  // Get current item (before early return)
  const currentItem = items.length > 0 ? items[currentIndex] : null;
  const nextIndex = items.length > 0 ? (currentIndex + 1) % items.length : 0;
  const prevIndex = items.length > 0 ? (currentIndex - 1 + items.length) % items.length : 0;
  const nextItem = items.length > 0 ? items[nextIndex] : null;
  const prevItem = items.length > 0 ? items[prevIndex] : null;
  
  // Debug: Log current item type (before early return)
  useEffect(() => {
    if (currentItem && items.length > 0) {
      const isVideo = (currentIndex === 0 && videoUrl) || currentItem.type === 'video';
      console.log(`Current carousel item: index=${currentIndex}, type=${currentItem.type}, isVideo=${isVideo}, hasVideoUrl=${!!videoUrl}`);
    }
  }, [currentIndex, currentItem, videoUrl, items.length]);

  if (!items || items.length === 0) {
    return <p>No content available.</p>;
  }

  return (
    <div className="product-carousel-container">
      {/* Navigation Info */}
      <div className="carousel-info">
        <span className="carousel-counter">
          {currentIndex + 1} / {items.length}
        </span>
      </div>

      {/* Carousel Wrapper */}
      <div className="carousel-wrapper">
        {/* Previous Card (for depth effect) */}
        {items.length > 1 && (
          <div className={`carousel-card carousel-card-prev ${direction === 'prev' && isAnimating ? 'swiveling' : ''}`}>
            <div className="carousel-card-inner">
              {prevItem.type === 'video' ? (
                <div className="carousel-card-video">
                  <video 
                    src={prevItem.url} 
                    controls 
                    className="carousel-video-player"
                    preload="metadata"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : prevItem.type === 'summary' ? (
                renderSummaryCard(prevItem)
              ) : (
                <>
                  <div className="carousel-card-price-badge">
                    {cleanText(prevItem.pricePoint)}
                  </div>
                  <div className="carousel-card-content">
                    <div className="carousel-card-brand">{cleanText(prevItem.brand)}</div>
                    <div className="carousel-card-product">{cleanText(prevItem.product)}</div>
                    <div className="carousel-card-why">
                      <div className="carousel-card-why-label">Why It Works</div>
                      <div className="carousel-card-why-text">{cleanText(prevItem.whyItWorks)}</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Current Card */}
        <div className={`carousel-card carousel-card-current ${isAnimating ? `swiveling-${direction}` : ''}`}>
          <div className="carousel-card-inner">
            {currentItem.type === 'video' ? (
              <div className="carousel-card-video">
                <video 
                  ref={videoRef}
                  src={currentItem.url} 
                  controls 
                  className="carousel-video-player"
                  autoPlay
                  preload="metadata"
                  playsInline
                  onPlay={() => {
                    if (trendId && videoRef.current) {
                      const duration = Math.round(videoRef.current.duration || 0);
                      console.log('Video play event:', { trendId, duration });
                      logVideoInteraction(trendId, 'play', 0, duration, 0);
                    } else {
                      console.warn('Video play event but missing trendId or videoRef:', { trendId: !!trendId, hasRef: !!videoRef.current });
                    }
                  }}
                  onPause={() => {
                    if (trendId && videoRef.current) {
                      const currentTime = Math.round(videoRef.current.currentTime || 0);
                      const duration = Math.round(videoRef.current.duration || 0);
                      const percentage = duration > 0 ? Math.round((currentTime / duration) * 100) : 0;
                      console.log('Video pause event:', { trendId, currentTime, duration, percentage });
                      logVideoInteraction(trendId, 'pause', currentTime, duration, percentage);
                    } else {
                      console.warn('Video pause event but missing trendId or videoRef:', { trendId: !!trendId, hasRef: !!videoRef.current });
                    }
                  }}
                  onEnded={() => {
                    if (trendId && videoRef.current) {
                      const duration = Math.round(videoRef.current.duration || 0);
                      console.log('Video ended event:', { trendId, duration });
                      logVideoInteraction(trendId, 'ended', duration, duration, 100);
                    } else {
                      console.warn('Video ended event but missing trendId or videoRef:', { trendId: !!trendId, hasRef: !!videoRef.current });
                    }
                  }}
                  onLoadedMetadata={() => {
                    if (videoRef.current) {
                      console.log('Video metadata loaded:', { 
                        duration: videoRef.current.duration,
                        videoWidth: videoRef.current.videoWidth,
                        videoHeight: videoRef.current.videoHeight
                      });
                    }
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : currentItem.type === 'summary' ? (
              renderSummaryCard(currentItem)
            ) : (
              <>
                <div className="carousel-card-price-badge">
                  {cleanText(currentItem.pricePoint)}
                </div>
                <div className="carousel-card-content">
                  <div className="carousel-card-brand">{cleanText(currentItem.brand)}</div>
                  <div className="carousel-card-product">{cleanText(currentItem.product)}</div>
                  <div className="carousel-card-why">
                    <div className="carousel-card-why-label">Why It Works</div>
                    <div className="carousel-card-why-text">{cleanText(currentItem.whyItWorks)}</div>
                  </div>
                </div>
                <div className="carousel-card-corner-accent"></div>
              </>
            )}
          </div>
        </div>

        {/* Next Card (for depth effect) */}
        {items.length > 1 && (
          <div className={`carousel-card carousel-card-next ${direction === 'next' && isAnimating ? 'swiveling' : ''}`}>
            <div className="carousel-card-inner">
              {nextItem.type === 'video' ? (
                <div className="carousel-card-video">
                  <video 
                    src={nextItem.url} 
                    controls 
                    className="carousel-video-player"
                    preload="metadata"
                    muted
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : nextItem.type === 'summary' ? (
                renderSummaryCard(nextItem)
              ) : (
                <>
                  <div className="carousel-card-price-badge">
                    {cleanText(nextItem.pricePoint)}
                  </div>
                  <div className="carousel-card-content">
                    <div className="carousel-card-brand">{cleanText(nextItem.brand)}</div>
                    <div className="carousel-card-product">{cleanText(nextItem.product)}</div>
                    <div className="carousel-card-why">
                      <div className="carousel-card-why-label">Why It Works</div>
                      <div className="carousel-card-why-text">{cleanText(nextItem.whyItWorks)}</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="carousel-navigation">
        <button
          className="carousel-nav-button carousel-nav-prev"
          onClick={goToPrev}
          disabled={isAnimating || items.length <= 1}
          aria-label="Previous"
        >
          <ChevronLeftIcon size={24} color="currentColor" />
        </button>
        <button
          className="carousel-nav-button carousel-nav-next"
          onClick={goToNext}
          disabled={isAnimating || items.length <= 1}
          aria-label="Next"
        >
          <ChevronRightIcon size={24} color="currentColor" />
        </button>
      </div>

      {/* Dots Indicator */}
      {items.length > 1 && (
        <div className="carousel-dots">
          {items.map((item, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentIndex ? 'active' : ''} ${item.type === 'video' ? 'carousel-dot-video' : ''}`}
              onClick={() => {
                if (!isAnimating) {
                  const direction = index > currentIndex ? 'next' : 'prev';
                  setDirection(direction);
                  setIsAnimating(true);
                  setTimeout(() => {
                    setCurrentIndex(index);
                    setIsAnimating(false);
                    
                    // Log carousel interaction - ensure we get the correct type
                    if (trendId && items.length > index) {
                      const targetItem = items[index];
                      // Explicitly check: if index is 0 and videoUrl exists, it's a video
                      let cardType = 'product';
                      if (index === 0 && videoUrl) {
                        cardType = 'video';
                      } else if (targetItem && targetItem.type) {
                        cardType = targetItem.type;
                      }
                      console.log(`Carousel dot click: index=${index}, type=${cardType}, hasVideo=${!!videoUrl}, itemType=${targetItem?.type}`);
                      logCarouselInteraction(
                        trendId,
                        index,
                        cardType,
                        'dot_click'
                      );
                    }
                  }, 400);
                }
              }}
              aria-label={`Go to ${item.type === 'video' ? 'video' : `product ${index}`}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default ProductCarousel;

